import React from 'react';
import { cn } from 'lib/utils';

interface QuestionStatusLineProps {
  ready?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * A short line under a question's inputs that says what is still needed, or confirms progress.
 * It is a polite live region, so screen readers hear the change without focus moving.
 */
export const QuestionStatusLine: React.FC<QuestionStatusLineProps> = ({ ready, children, className }) => (
  <p
    role="status"
    aria-live="polite"
    data-testid="question-status-line"
    className={cn(
      'mt-4 flex w-full items-center gap-2 text-sm leading-5',
      ready ? 'text-foundation-base-darkbase-dark-500' : 'text-foundation-base-darkbase-dark-300',
      className,
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        'inline-grid h-5 w-5 shrink-0 place-items-center rounded-full border text-[11px] font-semibold',
        ready
          ? 'border-transparent bg-foundation-base-darkbase-dark-500 text-white'
          : 'border-foundation-base-darkbase-dark-200',
      )}
    >
      {ready ? '✓' : ''}
    </span>
    <span>{children}</span>
  </p>
);

/** Round check for selectable rows. */
export const SelectionCheck: React.FC<{ checked: boolean; size?: number }> = ({ checked, size = 22 }) => (
  <span
    aria-hidden="true"
    style={{ width: size, height: size }}
    className={cn(
      'grid shrink-0 place-items-center rounded-full border-2 transition-colors',
      checked
        ? 'border-foundation-orange-500 bg-foundation-orange-500 text-white'
        : 'border-foundation-base-darkbase-dark-100 bg-white',
    )}
  >
    {checked ? (
      <svg viewBox="0 0 16 16" width={size * 0.6} height={size * 0.6} fill="none" focusable="false">
        <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ) : null}
  </span>
);

export const pluralize = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;
